import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useEditorStore } from '../store/editorStore';
import { usePlayheadStore } from '../store/playheadStore';
import { loadProject } from '../engine/projectStorage';
import { hasAssetData } from '../engine/proxyManager';
import { clearActiveAsset } from '../renderer/rendererBridge';

type LoaderState = 'loading' | 'ready' | 'error';

/**
 * Loads a project by ID from URL params into the editor store.
 * Only used when projectId is present in the route.
 */
export function useProjectLoader() {
  const { projectId } = useParams<{ projectId: string }>();
  const [state, setState] = useState<LoaderState>('loading');
  const [error, setError] = useState<string | null>(null);
  const loadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setState('ready');
      return;
    }

    // Skip if already loaded this project
    if (loadedIdRef.current === projectId) return;
    loadedIdRef.current = projectId;
    setState('loading');

    (async () => {
      try {
        const saved = await loadProject(projectId);
        if (!saved) {
          setError('projectNotFound');
          setState('error');
          return;
        }
        // Reset playhead + renderer from any previous project
        clearActiveAsset();
        useEditorStore.getState().loadProject(saved.project);
        // Background asset integrity check
        checkAssetIntegrity(saved.project.assets);
        setState('ready');
      } catch (err) {
        console.error('Project loader error:', err);
        setError('projectLoadError');
        setState('error');
      }
    })();
  }, [projectId]);

  return { state, error };
}

/** Background check: mark assets whose proxy data is missing */
async function checkAssetIntegrity(assets: { id: string; ingestStatus: string }[]) {
  for (const asset of assets) {
    if (asset.ingestStatus !== 'ready') continue;
    const exists = await hasAssetData(asset.id);
    if (!exists) {
      useEditorStore.getState().updateAssetStatus(asset.id, 'error', 0, 'assetDataMissing');
    }
  }
}
